import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { DOMAINS, getDomainById } from '../../../shared/constants/domains';
import SearchInput from '../../../shared/components/SearchInput';

/**
 * Home screen showing all comparison sets as cards, filterable by domain and search.
 */
export default function MatrixHome({ comparisons, drillStats }) {
  const [search, setSearch] = useState('');
  const [domainFilter, setDomainFilter] = useState('all');

  const filtered = useMemo(() => {
    let result = [...comparisons];

    if (domainFilter !== 'all') {
      result = result.filter(c => c.domain === domainFilter);
    }

    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(c =>
        c.title.toLowerCase().includes(q) ||
        c.protocols.some(p => p.toLowerCase().includes(q)) ||
        c.attributes.some(a =>
          a.name.toLowerCase().includes(q) ||
          Object.values(a.values).some(v => v.toLowerCase().includes(q))
        )
      );
    }

    return result;
  }, [comparisons, search, domainFilter]);

  return (
    <div className="px-4 py-6 max-w-4xl mx-auto">
      <div className="text-center mb-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-1">Protocol Comparison Matrix</h1>
        <p className="text-gray-500 text-sm">Interactive reference & drill for "which protocol does X?"</p>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-3 mb-6">
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Search protocols, attributes, values..."
          className="flex-1"
        />
        <select
          value={domainFilter}
          onChange={e => setDomainFilter(e.target.value)}
          className="px-3 py-2 rounded-lg border border-gray-300 bg-white text-gray-900 text-sm"
        >
          <option value="all">All Domains</option>
          {DOMAINS.map(d => (
            <option key={d.id} value={d.id}>{d.name}</option>
          ))}
        </select>
      </div>

      {/* Comparison cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {filtered.map(comparison => {
          const domain = getDomainById(comparison.domain);
          const hotCount = comparison.attributes.filter(a => a.exam_hot).length;
          const stats = drillStats?.[comparison.id];

          return (
            <Link
              key={comparison.id}
              to={`/matrix/${comparison.id}`}
              className="block bg-white border border-gray-200 rounded-xl p-5 hover:border-gray-400 hover:shadow-sm transition-all"
            >
              <div className="flex items-start justify-between mb-2">
                <h3 className="font-semibold text-gray-900">{comparison.title}</h3>
                {domain && (
                  <span
                    className="text-xs px-2 py-0.5 rounded-full"
                    style={{
                      backgroundColor: `${domain.color}15`,
                      color: domain.color,
                    }}
                  >
                    {domain.shortName}
                  </span>
                )}
              </div>

              <div className="flex flex-wrap gap-1 mb-3">
                {comparison.protocols.map(p => (
                  <span key={p} className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-600">
                    {p}
                  </span>
                ))}
              </div>

              <div className="flex items-center gap-4 text-xs text-gray-400">
                <span>{comparison.attributes.length} attributes</span>
                <span>★ {hotCount} exam-critical</span>
                {stats?.accuracy != null && (
                  <span className={stats.accuracy >= 0.8 ? 'text-green-500' : stats.accuracy >= 0.5 ? 'text-yellow-500' : 'text-red-500'}>
                    {Math.round(stats.accuracy * 100)}% drill accuracy
                  </span>
                )}
              </div>
            </Link>
          );
        })}
      </div>

      {filtered.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          No comparisons match your search.
        </div>
      )}
    </div>
  );
}
